import { supabase } from '@/integrations/supabase/client';

const enterpriseDb = supabase as any;

export interface AgentBranchOption {
  id: string;
  name: string;
}

export interface AgentBranchAssignment {
  agent_id: string;
  branch_id: string;
  branch_name: string;
  unit_id: string | null;
}

interface EnterpriseAgentAccessClient {
  rpc(
    functionName: string,
    parameters: Readonly<Record<string, unknown>>,
  ): Promise<{ data: unknown; error: { message?: string } | null }>;
}

const enterpriseAccessDb = supabase as unknown as EnterpriseAgentAccessClient;

export async function fetchEnterpriseBranches(tenantId: string): Promise<AgentBranchOption[]> {
  const { data, error } = await enterpriseDb
    .from('companies')
    .select('id, name')
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .not('parent_company_id', 'is', null)
    .order('name');

  if (error) throw error;
  return (data || []) as AgentBranchOption[];
}

export async function fetchAgentBranchIds(agentId: string): Promise<string[]> {
  const { data, error } = await enterpriseDb
    .from('enterprise_asset_assignments')
    .select('branch_id')
    .eq('asset_type', 'agent')
    .eq('asset_id', agentId);

  if (error) throw error;
  return [...new Set((data || []).map((row: { branch_id: string }) => row.branch_id).filter(Boolean))];
}

export async function fetchAgentBranchAssignments(
  tenantId: string,
  agentIds: string[],
): Promise<AgentBranchAssignment[]> {
  if (agentIds.length === 0) return [];

  const [assignmentResult, branchResult] = await Promise.all([
    enterpriseDb
      .from('enterprise_asset_assignments')
      .select('asset_id, branch_id, unit_id')
      .eq('tenant_id', tenantId)
      .eq('asset_type', 'agent')
      .in('asset_id', agentIds),
    fetchEnterpriseBranches(tenantId),
  ]);

  if (assignmentResult.error) throw assignmentResult.error;
  const branchNames = new Map(branchResult.map((branch) => [branch.id, branch.name]));

  return (assignmentResult.data || [])
    .map((row: { asset_id: string; branch_id: string; unit_id: string | null }) => ({
      agent_id: row.asset_id,
      branch_id: row.branch_id,
      branch_name: branchNames.get(row.branch_id) || 'Unknown branch',
      unit_id: row.unit_id,
    }))
    .filter((row: AgentBranchAssignment, index: number, rows: AgentBranchAssignment[]) => (
      rows.findIndex((candidate) => (
        candidate.agent_id === row.agent_id
        && candidate.branch_id === row.branch_id
        && candidate.unit_id === row.unit_id
      )) === index
    ));
}

export async function canManageEnterpriseAgent(
  tenantId: string,
  agentId: string,
): Promise<boolean> {
  const { data, error } = await enterpriseAccessDb.rpc('can_manage_enterprise_asset', {
    p_tenant_id: tenantId,
    p_asset_type: 'agent',
    p_asset_id: agentId,
  });

  if (error) throw new Error(error.message || 'Unable to verify agent editing access.');
  if (typeof data !== 'boolean') {
    throw new Error('The server returned an invalid agent editing permission.');
  }
  return data;
}

export async function replaceAgentBranchAssignments(
  agentId: string,
  branchIds: string[],
): Promise<string[]> {
  const { data, error } = await enterpriseDb.rpc('replace_agent_branch_assignments', {
    p_agent_id: agentId,
    p_branch_ids: [...new Set(branchIds)],
  });

  if (error) throw error;
  return (data || []).map((row: { branch_id: string }) => row.branch_id);
}
